<script>
import { mapGetters } from 'vuex';
import { useAlert, useTrack } from 'dashboard/composables';
import NotificationsAPI from 'dashboard/api/notifications';
import ConfirmationDialog from 'dashboard/components-next/dialog/Dialog.vue';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';

import NotificationTable from './NotificationTable.vue';

import { ACCOUNT_EVENTS } from '../../../../helper/AnalyticsHelper/events';
export default {
  components: {
    NotificationTable,
    TableFooter,
    ConfirmationDialog,
  },
  data() {
    return { pendingDelete: null, isDeleting: false };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      meta: 'notifications/getMeta',
      records: 'notifications/getNotifications',
      uiFlags: 'notifications/getUIFlags',
    }),
  },
  mounted() {
    this.$store.dispatch('notifications/get', { page: 1 });
  },
  methods: {
    requestDelete(notification) {
      if (this.isDeleting) return;
      this.pendingDelete = notification;
      this.$refs.deleteDialog.open();
    },
    async confirmDelete() {
      if (!this.pendingDelete || this.isDeleting) return;
      this.isDeleting = true;
      const notification = this.pendingDelete;
      try {
        await NotificationsAPI.delete(notification.id);
      } catch {
        useAlert(this.$t('NOTIFICATIONS_PAGE.DELETE_ACTION.ERROR'));
        this.isDeleting = false;
        return;
      }
      const count = Math.max(0, Number(this.meta.count) - 1);
      this.$store.commit('notifications/DELETE_NOTIFICATION', {
        notification,
        count,
        unread_count: Math.max(
          0,
          Number(this.meta.unreadCount) - (notification.read_at ? 0 : 1)
        ),
      });
      this.$refs.deleteDialog.close();
      this.pendingDelete = null;
      useAlert(this.$t('NOTIFICATIONS_PAGE.DELETE_ACTION.SUCCESS'));
      const page = Math.min(
        Number(this.meta.currentPage) || 1,
        Math.max(1, Math.ceil(count / 15))
      );
      try {
        window.history.replaceState({}, '', `${this.$route.path}?page=${page}`);
        await this.$store.dispatch('notifications/get', { page });
        await this.$store.dispatch('notifications/unReadCount');
      } finally {
        this.isDeleting = false;
      }
    },
    onPageChange(page) {
      window.history.pushState({}, null, `${this.$route.path}?page=${page}`);
      this.$store.dispatch('notifications/get', { page });
    },
    refreshNotifications() {
      this.$store.dispatch('notifications/get', {
        page: Number(this.meta.currentPage) || 1,
      });
    },
    markNotificationRead(notification) {
      const {
        primary_actor_id: primaryActorId,
        primary_actor_type: primaryActorType,
      } = notification;
      this.$store.dispatch('notifications/read', {
        id: notification.id,
        primaryActorId,
        primaryActorType,
        unreadCount: this.meta.unreadCount,
      });
    },
    openNotification(notification) {
      const {
        primary_actor: primaryActor,
        notification_type: notificationType,
      } = notification;

      useTrack(ACCOUNT_EVENTS.OPEN_CONVERSATION_VIA_NOTIFICATION, {
        notificationType,
      });
      this.markNotificationRead(notification);

      if (notificationType === 'system_alert') {
        const detailsUrl = primaryActor?.actions?.details_url;
        if (detailsUrl)
          window.open(detailsUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      this.$router.push(
        `/app/accounts/${this.accountId}/conversations/${primaryActor.id}`
      );
    },
    openRelatedConversation(notification, displayId) {
      this.markNotificationRead(notification);
      this.$router.push(
        `/app/accounts/${this.accountId}/conversations/${displayId}`
      );
    },
    requestClearAll() {
      if (this.isDeleting) return;
      this.$refs.clearAllDialog.open();
    },
    // O servidor apaga em segundo plano; a lista e os contadores são zerados aqui,
    // sem recarregar, para não mostrar itens que ainda estão sendo removidos.
    async confirmClearAll() {
      if (this.isDeleting) return;
      this.isDeleting = true;
      try {
        await NotificationsAPI.deleteAll({ type: 'all' });
        this.$store.commit('notifications/DELETE_ALL_NOTIFICATIONS');
        this.$store.commit('notifications/SET_NOTIFICATIONS_META', {
          count: 0,
          current_page: 1,
          unread_count: 0,
        });
        window.history.replaceState({}, '', `${this.$route.path}?page=1`);
        this.$refs.clearAllDialog.close();
        useAlert(this.$t('NOTIFICATIONS_PAGE.CLEAR_ALL.SUCCESS'));
      } catch {
        useAlert(this.$t('NOTIFICATIONS_PAGE.CLEAR_ALL.ERROR'));
      } finally {
        this.isDeleting = false;
      }
    },
    onMarkAllDoneClick() {
      useTrack(ACCOUNT_EVENTS.MARK_AS_READ_NOTIFICATIONS);
      this.$store.dispatch('notifications/readAll');
    },
  },
};
</script>

<template>
  <div class="w-full min-w-0 flex-1 bg-transparent">
    <div class="flex min-h-full flex-col">
      <NotificationTable
        :notifications="records"
        :is-loading="uiFlags.isFetching"
        :is-updating="uiFlags.isUpdating"
        :on-click-notification="openNotification"
        :on-click-related-conversation="openRelatedConversation"
        :on-mark-all-done-click="onMarkAllDoneClick"
        :on-refresh="refreshNotifications"
        :on-delete-notification="requestDelete"
        :on-clear-all="requestClearAll"
        :is-deleting="isDeleting"
      />
      <ConfirmationDialog
        ref="clearAllDialog"
        type="alert"
        :title="$t('NOTIFICATIONS_PAGE.CLEAR_ALL.TITLE')"
        :description="
          $t('NOTIFICATIONS_PAGE.CLEAR_ALL.DESCRIPTION', {
            count: meta.count || 0,
          })
        "
        :confirm-button-label="$t('NOTIFICATIONS_PAGE.CLEAR_ALL.CONFIRM')"
        :is-loading="isDeleting"
        :disable-confirm-button="isDeleting"
        :show-cancel-button="!isDeleting"
        :close-on-click-outside="!isDeleting"
        @confirm="confirmClearAll"
      />
      <ConfirmationDialog
        ref="deleteDialog"
        type="alert"
        :title="$t('NOTIFICATIONS_PAGE.DELETE_ACTION.TITLE')"
        :description="$t('NOTIFICATIONS_PAGE.DELETE_ACTION.DESCRIPTION')"
        :confirm-button-label="$t('NOTIFICATIONS_PAGE.DELETE_ACTION.LABEL')"
        :is-loading="isDeleting"
        :disable-confirm-button="isDeleting"
        :show-cancel-button="!isDeleting"
        :close-on-click-outside="!isDeleting"
        @confirm="confirmDelete"
      />
      <TableFooter
        v-if="meta.count"
        class="mx-auto mb-8 w-full max-w-6xl border-t border-n-weak px-4 sm:px-6"
        :current-page="Number(meta.currentPage)"
        :total-count="meta.count"
        :page-size="15"
        @page-change="onPageChange"
      />
    </div>
  </div>
</template>
